/* eslint-disable no-nested-ternary */
/* eslint-disable max-classes-per-file */
/* eslint-disable no-param-reassign */
import {
  addDays,
  addWeeks,
  addYears,
  differenceInWeeks,
  differenceInYears,
  format,
} from 'date-fns';
import { expDateOrder } from '../../../utils/formatCharts/orderDatetime';
import { labelValuesStr } from './handleCustomValue';
import formatTableValue from '../../../../utils/functions/formatTableValue';
import getStateInitials from '../../charts/Map/mapConfigs/getStateInitials';
import compare from '../../../../utils/functions/sorting';
import TextOverflow from '../../../core/TextOverflow';
import { fillerMap } from '../../../../options';

/**
 * Takes a raw array of columns and normalizes the structure.
 * @param {array<string>|array<array<string>} rawColumns Array of columns from the data matrix.
 * @param {boolean} [withTotals] If true, keep the `Totais` column.
 * @returns {string[]} A array of columns names, in string.
 */
const normalizeColumns = (rawColumns, withTotals = false) => (
  rawColumns.reduce((aux, columnDt) => {
    if (['number', 'string'].includes(typeof columnDt)) aux.push(columnDt);
    else {
      const k = columnDt[1];
      if (k !== 'Totais' || withTotals) aux.push(k);
    }
    return aux;
  }, [])
);

const getMinMaxDates = (dates) => {
  dates = dates.filter((d) => !Number.isNaN(d.getTime()));
  if (dates.length < 1) return [0, 0];
  return [
    new Date(Math.min(...dates)),
    new Date(Math.max(...dates)),
  ];
};

const dateIntervals = (lines, key) => {
  switch (key) {
    case 'year': {
      const dates = lines.map((y) => new Date(`${y}-01-01T00:00`));
      const [ini, end] = getMinMaxDates(dates);
      const interval = differenceInYears(end, ini);
      return Array.from({ length: interval + 1 }, (_, i) => (
        format(addYears(ini, i), 'yyyy')
      ));
    }
    case 'W': {
      const dates = lines.map((y) => new Date(`${y.split('/')?.[0]}T00:00`));
      const [ini, end] = getMinMaxDates(dates);
      const interval = differenceInWeeks(end, ini);
      return Array.from({ length: interval + 1 }, (_, i) => {
        const first = addWeeks(ini, i);
        return `${format(first, 'yyyy-MM-dd')}/${format(addDays(first, 6), 'yyyy-MM-dd')}`;
      });
    }
    default:
      return lines;
  }
};

const lineFiller = (lines, key) => {
  let filler = fillerMap[key] ?? [];
  if (['M', 'Q', 'semester'].includes(key)) {
    let years = [...new Set(lines.map((l) => l.split('-')[0]))];
    years = years.filter((y) => !Number.isNaN(+y));
    years = dateIntervals(years, 'year');
    filler = filler.flatMap((m) => years.map((y) => `${y}-${m}`));
  } else if (['year', 'W'].includes(key)) {
    filler = dateIntervals(lines, key);
  }
  filler = filler.filter((f) => !lines.includes(f));
  return [...filler, ...lines];
};

const columnFiller = (columns, key) => {
  let extra = null;
  if (Array.isArray(columns[0])) [[extra]] = columns;
  const keys = normalizeColumns(columns, true);
  if (key in fillerMap) {
    const cols = lineFiller(keys, key);
    return cols.map((m) => (extra ? [extra, m] : [m]));
  } return columns;
};

export const dataFiller = (rawData, config) => {
  const { index, data, columns } = rawData;
  const aux = { index, data, columns };
  aux.index = lineFiller(index, config.ExtendDateLine);
  aux.columns = columnFiller(columns, config.ExtendDateColumn);
  aux.data = [
    ...(new Array(aux.index.length - index.length).fill(
      new Array(aux.columns.length).fill(0),
    )),
    ...data.map((dt) => [...new Array(aux.columns.length - dt.length).fill(0), ...dt]),
  ];
  return aux;
};

/**
 * Calculates totals based on actual data (not 'Totals' category).
 * @param {string[]} index Index from data matrix.
 * @param {array<string[]>} data Values from data matrix.
 * @param {string[]} columns Columns names.
 * @returns {object} Object that contains column totals and global totals.
 */
const getDataTotals = (index, data, columns) => (
  index.reduce((auxSum, idx, i) => {
    if (idx !== 'Totais') {
      columns.forEach((key, j) => {
        auxSum.idx[idx] = (auxSum.idx[idx] || 0) + (+data[i][j]);
        auxSum.global += (+data[i][j]);
        auxSum.col[key] = (auxSum.col[key] || 0) + (+data[i][j]);
      });
    }
    return auxSum;
  }, { global: 0, idx: {}, col: {} })
);

const getConditionalDataTotals = (index, data, columns, config) => {
  const checkInterval = (value) => ((
    config?.MinValueControl?.checked || value >= (config?.MinValueControl?.value ?? -Infinity)
  ) && (
    config?.MaxValueControl?.checked || value <= (config?.MaxValueControl?.value ?? Infinity)
  ));
  const tt = index.reduce((auxSum, idx, i) => {
    if (idx !== 'Totais') {
      columns.forEach((key, j) => {
        const val = (+data[i][j]);
        if (checkInterval(val)) {
          auxSum.idx[idx] = (auxSum.idx[idx] ?? 0) + val;
          auxSum.global += val;
          auxSum.col[key] = (auxSum.col[key] ?? 0) + val;
          if (key !== 'Totais') {
            auxSum.idxLen[idx] = (auxSum.idxLen[idx] ?? 0) + 1;
            auxSum.colLen[key] = (auxSum.colLen[key] ?? 0) + 1;
          }
        }
      });
    }
    return auxSum;
  }, {
    global: 0, idx: {}, col: {}, idxLen: {}, colLen: {},
  });
  tt.idxLen.Totais = Object.keys(tt.idxLen).length;
  tt.colLen.Totais = Object.keys(tt.colLen).length;
  return tt;
};

/**
 * Sorts data based on style configs.
 * @param {object[]} data Data for the chart.
 * @param {object} config Style config.
 * @param {boolean|false} invert When `true` inverts the sorting order.
 * **Visual only:** Use only when chart orientation appears to be inverted.
 * @returns {object[]} Data sorted.
 */
export const dataSort = (data, config, invert = false) => {
  if (config && data) {
    const copyData = [...data];
    if (config.CustomSortControl?.checked && config.CustomSortControl.kpiCategories) {
      const { kpiCategories } = config.CustomSortControl;
      return copyData.sort(
        (a, b) => kpiCategories.indexOf(a.label) - kpiCategories.indexOf(b.label),
      );
    }
    switch (config.SortValuesControl) {
      // 'Não ordenado' now is labeled as 'Automático'
      case 'Não ordenado':
      case 'Alfabética A-Z':
        return copyData.sort((a, b) => (invert ? -1 : 1) * (
          compare(expDateOrder(a.label), expDateOrder(b.label))
        ));
      case 'Alfabética Z-A':
        return copyData.sort((a, b) => (invert ? -1 : 1) * (
          compare(expDateOrder(b.label), expDateOrder(a.label))
        ));
      case 'Valor Crescente':
        return copyData.sort((a, b) => (invert ? -1 : 1) * (
          compare(expDateOrder(a.value), expDateOrder(b.value), true)
        ));
      case 'Valor Decrescente':
        return copyData.sort((a, b) => (invert ? -1 : 1) * (
          compare(expDateOrder(b.value), expDateOrder(a.value), true)
        ));
      default:
        return copyData;
    }
  }
  return data;
};

/**
 * Sorts sub data based on style configs.
 * @param {string[][]} data Data for the chart.
 * @param {string[]} columns Columns of the chart.
 * @param {object} config Style config.
 * @param {boolean|false} invert When `true` inverts the sorting order.
 * **Visual only:** Use only when chart orientation appears to be inverted.
 * @returns {[string[][], string[]]} Data sorted.
 */
export const subDataSort = (data, columns, config, invert = false) => {
  let columnsDt = columns.map((c, i) => ({
    label: c,
    oldIdx: i,
  }));
  columnsDt = dataSort(columnsDt, {
    SortValuesControl: config.SortColumnsControl,
    CustomSortControl: config.CustomSortColumnControl,
  }, invert);
  const orderedData = data.map((datum) => (
    columnsDt.map((c) => datum[c.oldIdx])
  ));
  return [orderedData, columnsDt.map((c) => c.label)];
};

/**
 * Receives a sorted data and calculates the variation
 * @param {object[]} fData Sorted data
 * @param {boolean} isPercentual If true, calculates the percentage variation
 * @param {string[]} [extraKeys] Extra value keys inside the data (categories)
 * @returns {object[]} Data with variation in value2
 */
export const calcVariation = (fData, isPercentual, extraKeys = []) => {
  const lasts = extraKeys.reduce((aux, k) => {
    aux[`${k}-value2`] = null;
    return aux;
  }, { value2: null });
  return fData.map((datum) => {
    const aux = { ...datum };
    Object.entries(lasts).forEach(([key, last]) => {
      let divisor = 1;
      if (isPercentual) divisor = (last !== 0 ? last : datum[key]) / 100;
      aux[key] = last === null ? 0 : ((datum[key] - last) / Math.abs(divisor) || 0);
      lasts[key] = datum[key];
    });
    return aux;
  });
};

export const calcCategoricalVariation = (fData, isPercentual, extraKeys = []) => (
  fData.map((datum) => {
    const aux = { ...datum };
    let last = null;
    extraKeys.forEach((k) => {
      const key = `${k}-value2`;
      let divisor = 1;
      if (isPercentual) divisor = (last !== 0 ? last : datum[key]) / 100;
      aux[key] = last === null ? 0 : (datum[key] - last) / Math.abs(divisor);
      last = datum[key];
    });
    aux.value2 = 0;
    return aux;
  })
);

/**
 * Receives a 1 dimension (value + line) data matrix and converts it to a chart format.
 * Only works for `Pie`, `Funnel` and `Waffle` charts.
 * @param {object} rawData Data matrix from the api.
 * @param {object} config Style config.
 * @param {boolean|false} invert When `true` inverts the sorting order.
 * **Visual only:** Use only when chart orientation appears to be inverted.
 * @returns {{
 *  ready: boolean;
 *  data: array<{
 *   id: string,
 *   label: string,
 *   value: number,
 *  }>;
 *  total: number,
 *  hasNegative: boolean;
 * }} Data formatted for the chart.
 */
export const genData1D = (rawData, config, invert = false) => {
  const { index, data, columns } = rawData;
  const dataTotal = getDataTotals(index, data, columns);
  const isMainPercentual = config?.DataFormat === 'percentual';
  const isAltPercentual = config?.DataFormatAlt === 'percentual';
  const isVariation = [
    config?.DataAbsoluteFormatAlt, config?.DataPercentFormatAlt,
  ].includes('variation');
  let hasNegative = false;

  let fData = index.reduce((aux, idx, i) => {
    if (idx !== 'Totais') {
      if (+data[i][0] < 0) hasNegative = true;
      aux.push({
        id: idx,
        label: idx,
        value: isMainPercentual ? +(
          (data[i][0] * 100) / dataTotal.global
        ) : +data[i][0],
        value2: isAltPercentual && !isVariation ? +(
          (data[i][0] * 100) / dataTotal.global
        ) : +data[i][0],
      });
    }
    return aux;
  }, []);

  // Alteracoes para arrumar inconsistências do waffle e funil (Decidido dia 01/04/2026)
  fData = dataSort(fData, config, !config?.InvertVariation);

  if (!isAltPercentual && config?.DataAbsoluteFormatAlt === 'variation') {
    fData = calcVariation(fData, false);
  } else if (isAltPercentual && config?.DataPercentFormatAlt === 'variation') {
    fData = calcVariation(fData, true);
  }

  fData = dataSort(fData, config, invert);

  return {
    ready: true, data: fData, total: isMainPercentual ? 100 : +dataTotal.global, hasNegative,
  };
};

/**
 * Receives a 1 dimension (value + line) data matrix and converts it to a `calendar` chart format.
 * @param {object} rawData Data matrix from the api.
 * @param {object} config Style config.
 * @returns {{
 *  ready: boolean;
 *  data: array<{
 *   id: string,
 *   label: string,
 *   day: string,
 *   value: number,
 *  }>;
 *  total: number,
 * }} Data formatted for the calendar chart.
 */
export const genData1DCalendar = (rawData, config) => {
  const genericData = genData1D(rawData, config);
  const calendarData = genericData.data?.reduce((aux, datum) => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(datum.id)) {
      aux.push({
        ...datum,
        day: datum.id,
      });
    }
    return aux;
  }, []);
  return { ...genericData, ready: calendarData.length > 0, data: calendarData };
};

/**
 * Receives a 1 dimension (value + line) data matrix and converts it to a `map` chart format.
 * @param {object} rawData Data matrix from the api.
 * @param {object} config Style config.
 * @param {string} field Key that determines the region of the map.
 * @returns {{
 *  ready: boolean;
 *  data: array<{
 *   id: string,
 *   label: string,
 *   value: number,
 *  }>;
 *  total: number,
 * }} Data formatted for the map chart.
 */
export const genData1DMap = (rawData, config, field) => {
  const genericData = genData1D(rawData, config);
  let min = 0;
  let max = config?.DataFormat === 'percentual' ? 10 : 0;

  const mapData = genericData.data?.map((datum) => {
    const valId = (`${datum.id}`).normalize('NFD').replace(/\p{Diacritic}/gu, '').trim().toLowerCase();
    min = Math.min(datum.value, min);
    max = Math.max(datum.value, max);
    return ({
      ...datum,
      id: field === 'UF' ? getStateInitials(valId) : valId,
    });
  });
  return {
    ...genericData, ready: mapData.length > 0, data: mapData, domain: [min, max],
  };
};

/**
 * Receives a 1 dimension (value + line) or a 2 dimension (value + line + column)
 * data matrix and converts it to a chart format.
 * Only works for `Radar` and `Bar` charts.
 * @param {object} rawData Data matrix from the api.
 * @param {object} config Style config.
 * @param {boolean|false} invert When `true` inverts the sorting order.
 * **Visual only:** Use only when chart orientation appears to be inverted.
 * @returns {{
 *  ready: boolean;
 *  data: array<{
 *   uid: string,
 *   label: string,
 *   value: number,
 *   nCategories: string,
 *  }>;
 *  keys: string[],
 *  biggests: {
 *    global: number,
 *    categories: number,
 *  }
 *  hasNegative: boolean;
 * }} Data formatted for the chart.
 */
export const genData2DWithKeys = (rawData, config, invert = false) => {
  const { index, columns, data: initialData } = rawData;
  let data = initialData;
  let keys = normalizeColumns(columns);
  const dataTotals = getDataTotals(index, data, keys);
  [data, keys] = subDataSort(data, keys, config);
  const biggests = { global: 0, categories: 0 };
  const isMainPercentual = config?.DataFormat === 'percentual';
  const isMainTgtGlobal = columns.length <= 1 || config?.DataPercentFormat === 'global';
  const isAltPercentual = config?.DataFormatAlt === 'percentual';
  const isAltTgtGlobal = columns.length <= 1 || config?.DataPercentFormatAlt === 'global';
  const isVariation = [
    config?.DataAbsoluteFormatAlt, config?.DataPercentFormatAlt,
  ].includes('variation');
  let hasNegative = false;

  let fData = index.reduce((aux, idx, i) => {
    if (idx !== 'Totais') {
      const datum = {
        uid: idx, label: idx, value: 0, value2: 0,
      };
      keys.forEach((key, j) => {
        if (+data[i][0] < 0) hasNegative = true;
        datum[key] = isMainPercentual ? (
          ((data[i][j] * 100) / (isMainTgtGlobal ? dataTotals.global : dataTotals.idx[idx])) || 0
        ) : (+data[i][j]) || 0;
        datum[`${key}-value2`] = isAltPercentual && !isVariation ? (
          ((data[i][j] * 100) / (isAltTgtGlobal ? dataTotals.global : dataTotals.idx[idx])) || 0
        ) : (+data[i][j]) || 0;
        datum.value += datum[key];
        datum.value2 += datum[`${key}-value2`];
        biggests.categories = Math.max(biggests.categories, datum[key]);
      });
      biggests.global = Math.max(biggests.global, datum.value);
      aux.push(datum);
    }
    return aux;
  }, []);

  fData = dataSort(fData, config, !!config?.InvertVariation);

  if (!isAltPercentual && config?.DataAbsoluteFormatAlt === 'variation') {
    fData = (
      config?.VariationByCategory && keys.length > 1 ? calcCategoricalVariation : calcVariation
    )(fData, false, keys);
  } else if (isAltPercentual && config?.DataPercentFormatAlt === 'variation') {
    fData = (
      config?.VariationByCategory && keys.length > 1 ? calcCategoricalVariation : calcVariation
    )(fData, true, keys);
  }

  fData = dataSort(fData, config, invert);

  return {
    ready: true, data: fData, keys, biggests, hasNegative, total: dataTotals.global,
  };
};

/**
 * Sorts points data (categories and sub-categories) based on style configs.
 * Only works for data generated by `genData2DWithPoints` function.
 * @param {object[]} data Data for the chart.
 * @param {object} config Style config.
 * @param {boolean|false} invert When `true` inverts the sorting order.
 * **Visual only:** Use only when chart orientation appears to be inverted.
 * @returns {object[]} Data sorted.
 */
export const dataSortPoints = (data, config, invert) => {
  const hasSortColumn = data.length > 1 && config.SortColumnsControl && config.SortColumnsControl !== 'Não ordenado';
  const subConfig = {
    SortValuesControl: hasSortColumn ? config.SortColumnsControl : config.SortValuesControl,
    CustomSortControl: hasSortColumn ? config.CustomSortColumnControl : config.SortValuesControl,
  };
  return (
    dataSort(
      data.map((datum) => ({
        ...datum,
        data: dataSort(datum.data, config, !invert),
      })),
      subConfig,
      invert,
    )
  );
};

/**
 * Receives a sorted data and calculates the variation
 * @param {object[]} fData Sorted data
 * @param {boolean} isPercentual If true, calculates the percentage variation
 * @returns {object[]} Data with variation in value2
 */
export const calcVariationPoints = (fData, isPercentual) => (
  fData.map((datum) => ({
    ...datum,
    data: calcVariation(datum.data, isPercentual),
  }))
);

/**
 * Receives a 1 dimension (value + line) or a 2 dimension (value + line + column)
 * data matrix and converts it to a chart format.
 * Only works for `Line` and `RadialBar` charts.
 * @param {object} rawData Data matrix from the api.
 * @param {object} config Style config.
 * @param {boolean|false} invert When `true` inverts the sorting order.
 * **Visual only:** Use only when chart orientation appears to be inverted.
 * @returns {{
 *  ready: boolean;
 *  data: array<{
 *   id: string,
 *   label: string,
 *   value: number,
 *   data: array<{
 *      id: string,
 *      label: string,
 *      value: number,
 *      x: string,
 *      y: number,
 *    }>
 *  }>;
 * biggest: number;
 * smallest: number;
 * hasNegative: boolean;
 * }} Data formatted for the chart.
 */
export const genData2DWithPoints = (rawData, config, invert = false) => {
  const { index, data, columns } = rawData;
  const keys = normalizeColumns(columns);
  const isMainPercentual = config?.DataFormat === 'percentual';
  const isMainTgtGlobal = config?.DataPercentFormat === 'global';
  const isAltPercentual = config?.DataFormatAlt === 'percentual';
  const isAltTgtGlobal = config?.DataPercentFormatAlt === 'global';
  const isVariation = [
    config?.DataAbsoluteFormatAlt, config?.DataPercentFormatAlt,
  ].includes('variation');
  let biggest = 0;
  let smallest = 0;
  let hasNegative = false;

  let categories = keys;
  let pointers = index;
  let is2D = false;
  let fData = [];
  if (columns.length !== keys.length) {
    categories = index;
    pointers = keys;
    is2D = true;
  }

  const dataTotals = getDataTotals(index, data, keys);

  fData = categories.reduce((cAux, cat, i) => {
    if (cat !== 'Totais') {
      const datum = { id: cat, label: cat, value: 0 };
      datum.data = pointers.reduce((pAux, pt, j) => {
        if (pt !== 'Totais') {
          if (+(is2D ? data[i][j] : data[j][i]) < 0) hasNegative = true;
          const val = (isMainPercentual ? (
            ((is2D ? data[i][j] : data[j][i]) * 100) / (
              isMainTgtGlobal ? dataTotals.global : dataTotals[is2D ? 'idx' : 'col'][cat]
            )
          ) : +(is2D ? data[i][j] : data[j][i])) || 0;
          const val2 = (isAltPercentual && !isVariation ? (
            ((is2D ? data[i][j] : data[j][i]) * 100) / (
              isAltTgtGlobal ? dataTotals.global : dataTotals[is2D ? 'idx' : 'col'][cat]
            )
          ) : +(is2D ? data[i][j] : data[j][i])) || 0;
          datum.value += val;
          pAux.push({
            id: pt,
            label: pt,
            x: pt,
            y: val,
            value: val,
            value2: val2,
          });
          biggest = Math.max(biggest, val);
          smallest = Math.min(smallest, val);
        }
        return pAux;
      }, []);
      cAux.push(datum);
    }
    return cAux;
  }, []);

  if (config?.TotalLineControl && keys.length > 1) {
    const totalLine = pointers.reduce((aux, k) => {
      if (k !== 'Totais') {
        const val = isMainPercentual ? (
          (dataTotals[is2D ? 'col' : 'idx'][k] * 100) / (
            isMainTgtGlobal ? dataTotals.global : dataTotals[is2D ? 'col' : 'idx'][k]
          )
        ) : +(dataTotals[is2D ? 'col' : 'idx'][k]);
        const val2 = isAltPercentual && !isVariation ? (
          (dataTotals[is2D ? 'col' : 'idx'][k] * 100) / (
            isAltTgtGlobal ? dataTotals.global : dataTotals[is2D ? 'col' : 'idx'][k]
          )
        ) : +(dataTotals[is2D ? 'col' : 'idx'][k]);
        biggest = Math.max(biggest, val);
        aux.push({
          id: k,
          label: k,
          x: k,
          y: val,
          value: val,
          value2: val2,
        });
      }
      return aux;
    }, []);
    fData.push({
      id: 'Totais',
      label: 'Totais',
      value: dataTotals.global,
      data: totalLine,
    });
  }

  fData = dataSortPoints(fData, config, !config?.InvertVariation);

  if (!isAltPercentual && config?.DataAbsoluteFormatAlt === 'variation') {
    fData = calcVariationPoints(fData, false);
  } else if (isAltPercentual && config?.DataPercentFormatAlt === 'variation') {
    fData = calcVariationPoints(fData, true);
  }

  fData = dataSortPoints(fData, config, invert);

  return {
    ready: true, data: fData, biggest, smallest, hasNegative, total: dataTotals.global,
  };
};

/**
 * Formats the table row attribute to correctly tag the Total label.
 * @param {string} index Atribute name.
 * @param {boolean} isAvg Checks if the value function is average (true) or other (false).
 * @returns Formatted attribute name.
 */
const formatIndexTotals = (index, isAvg) => {
  if (index === 'Totais' && isAvg) {
    return 'Médias';
  }
  return index;
};

/**
 * Receives a 1 dimension (value + line) or a 2 dimension (value + line + column)
 * data matrix and converts it to a `Table` rows format.
 * Only works for `Table` chart.
 * @param {object} rawData Data matrix from the api.
 * @param {object} config Style config.
 * @param {boolean} enableSorting Enables the "sort by click" function.
 * @param {boolean} isAvg Checks if the value function is average (true) or other (false).
 * @returns {{
 *  ready: boolean;
 *  cols: array<{
 *    field: string,
 *    label: string,
 *    sortable: boolean,
 *    valueGetter: function
 *  }>;
 * }} Data formatted for the chart.
 */
export const genDataColumns = (rawData, config, enableSorting, isAvg) => {
  const { index, columns, data } = rawData;
  const keys = normalizeColumns(columns, config.ColumnShowTotalControl);
  const dataTotals = getConditionalDataTotals(index, data, keys, config);
  let totalsExists = false;

  if (keys.length > 1) totalsExists = true;
  let cols = keys.reduce((aux, k) => {
    if (k === 'Totais') return aux;
    aux.push({
      field: k,
      label: k,
      sortable: enableSorting,
      isNumeric: true,
      valueGetter: (param) => {
        const len = dataTotals.colLen[k] ?? 0;
        return labelValuesStr(
          (param[k]?.value ?? 0) / (param.index === 'Totais' && (isAvg && len > 0) ? len : 1),
          (param[k]?.value2 ?? 0) / (param.index === 'Totais' && (isAvg && len > 0) ? len : 1),
          config,
        );
      },
    });
    return aux;
  }, []).sort((a, b) => compare(expDateOrder(a.field), expDateOrder(b.field)));

  cols = dataSort(cols, {
    SortValuesControl: config.SortColumnsControl,
    CustomSortControl: config.CustomSortColumnControl,
  });

  if (totalsExists && config.ColumnShowTotalControl && config.ColumnOnlyShowTotalControl) cols = [];

  cols.unshift({
    field: 'index',
    label: '-',
    sortable: enableSorting,
    isNumeric: false,
    valueGetter: (param) => formatIndexTotals(param.index, isAvg),
  });

  if (totalsExists && config.ColumnShowTotalControl) {
    cols.push({
      field: 'Totais',
      label: isAvg ? 'Médias' : 'Totais',
      sortable: enableSorting,
      isNumeric: true,
      valueGetter: (param) => {
        let divisor = 1;
        if (isAvg) {
          divisor = param.index === 'Totais' ? (
            dataTotals.idxLen.Totais * dataTotals.colLen.Totais
          ) : dataTotals.idxLen[param.index];
        }
        return labelValuesStr(
          (param.Totais?.value ?? 0) / (divisor ?? 1),
          (param.Totais?.value2 ?? 0) / (divisor ?? 1),
          config,
        );
      },
    });
  }

  return { ready: true, cols };
};

/** Auxiliary class for the table row data */
class TableData {
  constructor(value, value2) {
    this.value = value;
    this.value2 = value2;
  }

  toString() {
    return this.value.toString();
  }
}

const percentualTarget = (value, totals, row, col, target) => {
  switch (target) {
    case 'byRow':
      return (value * 100) / totals.idx[row];
    case 'byColumn':
      return (value * 100) / totals.col[col];
    case 'global':
    default:
      return (value * 100) / totals.global;
  }
};

/**
 * Receives a 1 dimension (value + line) or a 2 dimension (value + line + column)
 * data matrix and converts it to a `Table` columns format.
 * Only works for `Table` or `Value*` charts.
 * @param {object} rawData Data matrix from the api.
 * @param {boolean|false} globalTotalOnly Returns the global total
 * and removes the rows (for the `Value` chart).
 * @param {object} config Style config.
 * @returns {{
 *  ready: boolean;
 *  total: number;
 *  qtdElements: number;
 *  }|{
 *  ready: boolean;
 *  rowTotals: array<{
 *    string: string,
 *  }>;
 *  rows: array<{
 *    string: string,
 *  }>;
 *  qtdElements: number;
 * }} Data formatted for the chart.
 */
export const genDataRows = (rawData, globalTotalOnly = false, config) => {
  const { index, data, columns } = rawData;
  const keys = normalizeColumns(columns);
  const dataTotals = getConditionalDataTotals(index, data, keys, config);
  const rowTotals = { index: 'Totais', Totais: 0 };
  const isMainPercentual = config?.DataFormat === 'percentual';
  const isAltPercentual = config?.DataFormatAlt === 'percentual';
  // const isVariation = [
  //   config?.DataAbsoluteFormatAlt, config?.DataPercentFormatAlt,
  // ].includes('variation');
  const dataPercentTarget = keys.length > 1 ? config?.DataPercentFormat : 'global';
  const dataPercentTargetAlt = keys.length > 1 ? config?.DataPercentFormatAlt : 'global';

  const rows = index.reduce((aux, idx, i) => {
    if (idx !== 'Totais') {
      const datum = { index: idx };
      keys.forEach((k, j) => {
        datum[k] = new TableData(
          isMainPercentual ? percentualTarget(
            +data[i][j], dataTotals, idx, k, dataPercentTarget,
          ) : +data[i][j],
          isAltPercentual ? percentualTarget(
            +data[i][j], dataTotals, idx, k, dataPercentTargetAlt,
          ) : +data[i][j],
        );
        if (i === 0) {
          rowTotals[k] = new TableData(
            isMainPercentual ? (
              dataPercentTarget === 'byColumn' ? 100 : (dataTotals.col[k] * 100) / dataTotals.global
            ) : dataTotals.col[k],
            isAltPercentual ? (
              dataPercentTargetAlt === 'byColumn' ? 100 : (dataTotals.col[k] * 100) / dataTotals.global
            ) : dataTotals.col[k],
          );
        }
      });
      if (i === 0) {
        rowTotals.Totais = new TableData(
          isMainPercentual ? 100 : dataTotals.global,
          isAltPercentual ? 100 : dataTotals.global,
        );
      }
      datum.Totais = new TableData(
        isMainPercentual ? (
          dataPercentTarget === 'byRow' ? 100 : (dataTotals.idx[idx] * 100) / dataTotals.global
        ) : dataTotals.idx[idx],
        isAltPercentual ? (
          dataPercentTargetAlt === 'byRow' ? 100 : (dataTotals.idx[idx] * 100) / dataTotals.global
        ) : dataTotals.idx[idx],
      );
      aux.push(datum);
    }
    return aux;
  }, []);

  if (index.length === 1 && index[0] === 'Totais') {
    rowTotals.Totais = new TableData(
      isMainPercentual ? 100 : +data[0][0],
      isAltPercentual ? 100 : +data[0][0],
    );
    rowTotals[keys[0]] = rowTotals.Totais;
  }
  if (globalTotalOnly) {
    return {
      ready: true,
      total: rowTotals.Totais,
      qtdElements: dataTotals.idxLen.Totais * dataTotals.colLen.Totais,
    };
  }
  return {
    ready: true,
    rowTotals,
    rows,
    qtdElements: dataTotals.idxLen.Totais * dataTotals.colLen.Totais,
  };
};

/**
 * Receives a 1 dimension (value + line) or a 2 dimension (value + line + column)
 * data matrix and converts it to a chart format.
 * Only works for `Value` charts.
 * @param {object} rawData Data matrix from the api.
 * @param {object} config Style config.
 * @param {boolean} isValAvg Checks if the value function is average (true) or other (false).
 * @returns {{
 *  ready: boolean;
 *  value: number;
 * }} Data formatted for the chart.
 */
export const genDataValue = (rawData, config, isValAvg) => {
  const genericData = genDataRows(rawData, true, config);
  const val = genericData?.total?.value || 0;
  const valueFunction = config?.TotalFunctionControl || (isValAvg ? 'avg' : 'sum');

  return {
    ready: true,
    value: val / (valueFunction === 'avg' ? (genericData.qtdElements || 1) : 1),
  };
};

/**
 * Takes a raw array of indexes and normalizes the structure.
 * @param {array<string>|array<array<string>} rawIndexes Array of indexes from the data matrix.
 * @returns {string[][]} A array of columns names, in string.
 */
const normalizeIndexes = (rawIndexes) => {
  const unique = rawIndexes.reduce((aux, idxDt) => {
    if (typeof idxDt === 'string') aux[0].add(idxDt);
    else {
      aux[0].add(idxDt[0]);
      aux[1].add(idxDt[1]);
    }
    return aux;
  }, [new Set(), new Set()]);
  return [[...unique[0]], [...unique[1]]];
};

/**
 * Receives a specific data matrix and converts it to a `boxplot` chart format.
 * @param {object} rawData Data matrix from the api.
 * @param {object} config Style config.
 * @returns {{
 *  ready: boolean;
 *  data: array<{
 *   uid: string,
 *   label: string,
 *   group: string,
 *   subGroup: string,
 *   n: number,
 *   sd: number,
 *   mean: number,
 *   extrema: number[],
 *   values: number[],
 *   quantiles: number[],
 *  }>;
 *  indexes: string[],
 *  subIndexes: string[],
 *  biggest: number,
 *  smallest: number;
 *  isBoxplot: boolean;
 * }} Data formatted for the chart.
 */
export const genDataBoxPlot = (rawData, config) => {
  const { index, data, columns } = rawData;
  const keys = normalizeColumns(columns);
  const [indexes, subIndexes] = normalizeIndexes(index);

  let biggest = 0;
  let smallest = 0;
  const isBoxplot = keys.length === 10 && [
    'count', 'mean', 'std', 'min', '25%', '50%', '75%', 'max', 'lower', 'upper',
  ].every((val) => keys.includes(val));

  let fData = index.reduce((aux, idx, i) => {
    if (idx === 'Totais') return aux;
    const group = typeof idx === 'string' ? idx : idx[0];
    let subGroup = '';
    if (typeof columns?.[0] === 'string') {
      subGroup = idx;
    } else if (typeof idx === 'string') {
      [[subGroup]] = columns;
    } else {
      [, subGroup] = idx;
    }
    const datum = {
      uid: group,
      label: typeof idx === 'string' ? group : subGroup,
      group,
      subGroup,
      n: 0,
      sd: 0,
      mean: 0,
      extrema: [0, 0],
      values: [0, 0, 0, 0],
      quantiles: [0.25, 0.5, 0.75],
    };
    keys.forEach((key, j) => {
      const datumVal = (+data[i][j]);
      switch (key) {
        case 'count':
          datum.n = datumVal;
          break;
        case 'std':
          datum.sd = datumVal;
          break;
        case 'min':
          datum.extrema[0] = datumVal;
          break;
        case 'max':
          datum.extrema[1] = datumVal;
          break;
        case 'lower':
          datum.values[0] = datumVal;
          smallest = Math.min(smallest, datumVal);
          break;
        case '25%':
          datum.values[1] = datumVal;
          break;
        case '50%':
          datum.values[2] = datumVal;
          break;
        case '75%':
          datum.values[3] = datumVal;
          break;
        case 'upper':
          datum.values[4] = datumVal;
          biggest = Math.max(biggest, datumVal);
          break;
        default:
          datum[key] = datumVal;
          break;
      }
    });
    aux.push(datum);
    return aux;
  }, []);

  fData = dataSort(fData, config);
  return {
    ready: true, data: fData, indexes, subIndexes, biggest, smallest, isBoxplot,
  };
};

/**
 * Receives a specific data matrix and converts it to a `scatterplot` chart format.
 * @param {object} rawData Data matrix from the api.
 * @param {object} config Style config.
 * @returns {{
data
 * ready: boolean;
 * data: array<{
 *  id: string;
 *  uid: string,
 *  label: string,
 *  data: { x: string, y: string },
 * }>;
 * indexes: string[],
 * xMin: number,
 * xMax: number;
 * yMin: number,
 * yMax: number;
 * isScatterplot: boolean;
 * }}  Data formatted for the chart.
 */
export const genDataScatterPlot = (rawData, config) => {
  const { index, data } = rawData;
  const isScatterplot = Array.isArray(data[0]) && data[0].length === 2;

  const scales = {
    x: { min: 0, max: 0 },
    y: { min: 0, max: 0 },
  };
  let fData = [];
  let indexes = ['Valores'];
  if (typeof index[0] !== 'string') {
    fData.push({
      id: 'Valores',
      uid: 'Valores',
      label: 'Valores',
      data: data.map(([x, y]) => {
        scales.x.max = Math.max(scales.x.max, x);
        scales.x.min = Math.min(scales.x.min, x);
        scales.y.max = Math.max(scales.y.max, y);
        scales.y.min = Math.min(scales.y.min, y);
        return { x, y };
      }),
    });
  } else {
    const dataMap = index.reduce((aux, idx, i) => {
      scales.x.max = Math.max(scales.x.max, data[i][0]);
      scales.x.min = Math.min(scales.x.min, data[i][0]);
      scales.y.max = Math.max(scales.y.max, data[i][1]);
      scales.y.min = Math.min(scales.y.min, data[i][1]);
      if (aux[idx]) {
        aux[idx].data.push({ x: data[i][0], y: data[i][1] });
      } else {
        aux[idx] = {
          id: idx,
          uid: idx,
          label: idx,
          data: [{ x: data[i][0], y: data[i][1] }],
        };
      }
      return aux;
    }, {});
    fData = Object.values(dataMap);
    indexes = Object.keys(dataMap);
  }

  fData = dataSort(fData, config);
  return {
    ready: true,
    data: fData,
    indexes,
    xMin: scales.x.min,
    xMax: scales.x.max,
    yMin: scales.y.min,
    yMax: scales.y.max,
    isScatterplot,
  };
};

/**
 * Receives a specific data matrix and converts it to a sub table.
 * @param {object} rawData Data matrix from the api.
 * @param {object} config Style config.
 * @returns {{
 *  ready: boolean;
 *  cols: array<{
 *    field: string,
 *    label: string,
 *    sortable: boolean,
 *    valueGetter: function
 *  }>;
 *  rows: array<{
 *    string: string,
 *  }>;
 * }} Data formatted for the table.
 */
export const genDataSubset = (rawData, config) => {
  const {
    index, data, columns, types,
  } = rawData;
  const keys = normalizeColumns(columns);

  const cols = keys.map((k) => ({
    field: k,
    label: k,
    sortable: false,
    isNumeric: false,
    valueGetter: (param) => {
      const dt = config?.ValueFormatControl === 'locale' ? formatTableValue(param[k], types[k]) : param[k];
      if (config?.ColumnSize === 'fixed' && config?.RunningText) {
        return (
          <TextOverflow
            width={config?.ColumnWidth ?? 50}
            fontSize={config?.LabelsFontSizeControl?.value ?? 14}
          >
            {dt}
          </TextOverflow>
        );
      } return dt;
    },
  }));

  const rows = index.map((_, i) => {
    const datum = {};
    keys.forEach((k, j) => {
      datum[k] = data[i][j];
    });
    return datum;
  });

  return { ready: true, rows, cols: dataSort(cols, config) };
};

/**
 * Receives a float value data matrix and converts it to a chart format.
 * Only works for `Histogram` chart.
 * @param {object} rawData Data matrix from the api.
 * @param {object} config Style config.
 * **Visual only:** Use only when chart orientation appears to be inverted.
 * @returns {{
 *  ready: boolean;
 *  data: array<{
 *   id: string,
 *   label: string,
 *   value: number,
 *   min: number,
 *   max: number,
 *   bin: number,
 *  }>;
 *  isHistogram: boolean;
 *  keys: string,
 * }} Data formatted for the chart.
 */
export const genDataHistogram = (rawData, config) => {
  const { index, data, columns } = rawData;
  const [indexes, subIndexes] = normalizeIndexes(index);
  const keys = normalizeColumns(columns);
  const isHistogram = subIndexes.length > 0 && keys.length === 1;
  const bin = (subIndexes[subIndexes.length - 1] - indexes[0]) / index.length;
  const dataTotal = getDataTotals(index, data, columns);
  const isPercentual = config.DataFormatValueType === 'percentual';
  const isPercentualAlt = config.DataFormatValueTypeAlt === 'percentual';

  const fData = index.map((idx, i) => ({
    id: idx.toString(),
    label: `${indexes[i]}|${subIndexes[i]}`,
    min: +indexes[i],
    max: +subIndexes[i],
    [keys[0]]: isPercentual ? +(data[i][0] / dataTotal.global) : +data[i][0],
    [`${keys[0]}-value2`]: isPercentualAlt ? +(data[i][0] / dataTotal.global) : +data[i][0],
    bin,
  }));

  return {
    ready: true, data: fData, isHistogram, keys,
  };
};

/**
 * Receives a 1 dimension (value + line) or a 2 dimension (value + line + column)
 * data matrix and converts it to a chart format.
 * Only works for `Pareto` chart.
 * @param {object} rawData Data matrix from the api.
 * **Visual only:** Use only when chart orientation appears to be inverted.
 * @returns {{
 *  ready: boolean;
 *  data: array<{
 *   uid: string,
 *   label: string,
 *   value: number,
 *   nCategories: string,
 *   lineAbsolute: number,
 *   linePercent: number,
 *  }>;
 *  keys: string[],
 *  biggests: {
 *    global: number,
 *    categories: number,
 *  }
 *  hasNegative: boolean;
 * }} Data formatted for the chart.
 */
export const genParetoData = (rawData) => {
  const config = {
    DataFormat: 'absolute',
    DataPercentFormat: 'global',
    SortValuesControl: 'Valor Decrescente',
    CustomSortControl: { checked: false },
  };
  const barData = genData2DWithKeys(rawData, config, false);
  let cumulative = 0;
  const data = barData.data.map((dt) => {
    cumulative += dt.value;
    dt.lineAbsolute = cumulative;
    dt.linePercent = cumulative / barData.total;
    return dt;
  });
  return { ...barData, data };
};

/**
 * Receives a specific data matrix and converts it to a `gauge` chart format.
 * @param {object} rawData Data matrix from the api.
 * @param {object} config Style config.
 * @returns {{
 *  ready: boolean;
 *  data: array<{
 *   id: string,
 *   label: string,
 *   value: number,
 *   data: array<{
 *      id: string,
 *      label: string,
 *      value: number,
 *      value2: string,
 *      x: number,
 *      y: number,
 *    }>
 *  }>;
 *  total: number,
 *  extra: number,
 *  isGauge: boolean,
 *  metric: {
 *    value: number,
 *    value2: number,
 *  },
 *  hasNegative: boolean,
 * }} Data formatted for the chart.
 */
export const genDataGauge = (rawData, config) => {
  const { index, data, columns } = rawData;
  const keys = normalizeColumns(columns);
  const isPercentual = config?.DataFormat === 'percentual';
  const isPercentualAlt = config?.DataFormatAlt === 'percentual';
  let hasNegative = false;
  let fData = [];

  if (keys.length !== 2) {
    return {
      ready: true, data: fData, isGauge: false, indexes: [], hasNegative,
    };
  }
  const mIdx = keys.indexOf('_METRICS_');
  if (mIdx < 0) {
    return {
      ready: true, data: fData, isGauge: false, indexes: [], hasNegative,
    };
  }
  const idIdx = (mIdx + 1) % 2;

  const indexes = index.length === 1 ? [keys[idIdx]] : index.filter((idx) => idx !== 'Totais');
  const total = indexes.reduce((sum, _, i) => {
    sum += +(data[i][idIdx]);
    return sum;
  }, 0);

  const datum = { id: keys[idIdx], label: keys[idIdx], value: 0 };
  datum.data = indexes.map((idx, i) => {
    datum.value += +(data[i][mIdx]);
    const dtVal = +(data[i][idIdx]);
    if (dtVal < 0) hasNegative = true;
    const prc = (dtVal * 100) / total;
    return {
      id: idx,
      label: idx,
      x: idx,
      y: isPercentual ? prc : dtVal,
      value: isPercentual ? prc : dtVal,
      value2: isPercentualAlt ? prc : dtVal,
      stacked: dtVal, // temporary value to calculate the stacked percentage after sorting
      total,
    };
  });
  const metric = {
    value: isPercentual ? (datum.value * 100) / total : datum.value,
    value2: isPercentualAlt ? (datum.value * 100) / total : datum.value,
  };
  // metric.value = isPercentual ? metric.prc : datum.value;
  fData.push(datum);

  fData = dataSortPoints(fData, config, true);

  let stacked = 0;
  fData[0].data = fData[0].data.map((dt) => {
    stacked += dt.stacked;
    return {
      ...dt,
      stacked: isPercentual ? (stacked * 100) / total : stacked,
      stacked2: isPercentualAlt ? (stacked * 100) / total : stacked,
    };
  });

  return {
    ready: true,
    data: fData,
    metric,
    isGauge: true,
    total,
    hasNegative,
    extra: Math.max(metric.value, isPercentual ? 100 : total),
  };
};

/** Auxiliary class for the bullet datum */
class BulletDatum {
  constructor(key) {
    this.uid = key;
    this.label = key;
    this.id = key;
    this.data = [];
    this.ranges = [0];
    this.measures = [];
    this.markers = [];
    this.metric = 0;
  }
}

/**
 * Receives a specific data matrix and converts it to a `bullet` chart format.
 * @param {object} rawData Data matrix from the api.
 * @param {object} config Style config.
 * @returns {{
 *  ready: boolean;
 *  data: array<{
 *   uid: string,
 *   label: string,
 *   id: string,
 *   ranges: number[],
 *   measures: number[],
 *   markers: number[],
 *  }>;
 *  indexes: string[],
 *  isBullet: boolean,
 *  hasNegative: boolean,
 * }} Data formatted for the chart.
 */
export const genDataBullet = (rawData, config) => {
  const { index, data, columns } = rawData;
  const keys = normalizeColumns(columns);
  const isPercentual = config?.DataFormat === 'percentual';
  let hasNegative = false;
  const fData = [];

  if (keys.length !== 2) {
    return {
      ready: true, data: fData, isBullet: false, indexes: [], hasNegative, keyMap: {},
    };
  }
  const mIdx = keys.indexOf('_METRICS_');
  if (mIdx < 0) {
    return {
      ready: true, data: fData, isBullet: false, indexes: [], hasNegative, keyMap: {},
    };
  }
  const idIdx = (mIdx + 1) % 2;
  const indexes = index.length === 1 ? [keys[idIdx]] : index.filter((idx) => idx !== 'Totais');
  let biggest = 0;
  const temps = dataSort(indexes.map((idx, i) => {
    const val = +(data[i][idIdx]);
    biggest += val;
    return ({
      label: idx,
      value: val,
      value2: +(data[i][mIdx]),
    });
  }), config, false);

  if (config.GroupModeControl === 'stacked') {
    const datum = new BulletDatum(keys[idIdx]);
    temps.reduce((acc, tmp) => {
      if (tmp.value < 0) hasNegative = true;
      acc += tmp.value;
      const r = isPercentual ? (acc * 100) / biggest : acc;
      datum.ranges.push(r);
      datum.metric += tmp.value2;
      return acc;
    }, 0);
    datum[config?.MetricTypeControl === 'line' ? 'markers' : 'measures'].push(
      isPercentual ? (datum.metric * 100) / biggest : datum.metric,
    );
    fData.push(datum);
  } else {
    temps.forEach((tmp) => {
      const datum = new BulletDatum(tmp.label);
      if (tmp.value < 0) hasNegative = true;
      biggest = tmp.value;
      const r = isPercentual ? 100 : tmp.value;
      if (isPercentual) datum.metric += (tmp.value2 * 100) / biggest;
      else datum.metric += tmp.value2;
      datum.ranges.push(r);
      datum[config?.MetricTypeControl === 'line' ? 'markers' : 'measures'].push(datum.metric);
      fData.push(datum);
    });
  }

  return {
    ready: true,
    data: fData,
    isBullet: true,
    indexes: config.GroupModeControl === 'stacked' ? temps.map((tmp) => tmp.label) : [keys[idIdx]],
    hasNegative,
  };
};
